import React, { useState } from 'react';
import { AlertCircle, Phone, Syringe, Stethoscope, MessageCircle, Send } from 'lucide-react';
import { Button } from '../ui/button';
import PetAvatar from '../PetAvatar';
import { sendManualSms } from '../../services/api';
import { toast } from 'sonner';

const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.startsWith('0') ? '90' + cleaned.slice(1) : cleaned.startsWith('90') ? cleaned : '90' + cleaned;
};

const ReminderActions = ({ item, type }) => {
  const [sending, setSending] = useState(false);

  const getMessage = () => {
    const dueDateStr = item.next_due_date ? new Date(item.next_due_date).toLocaleDateString('tr-TR') : '';
    const savedUser = JSON.parse(localStorage.getItem('agrosvet_user') || '{}');
    const clinicName = savedUser?.clinic_name || 'Kliniğimiz';
    const opName = item.operation_type === 'vaccine' ? `${item.operation_name} aşısı` : item.operation_name;
    if (type === 'today') {
      return `Sayın ${item.customer_name}, ${item.pet_name} adlı dostunuzun ${opName} için bugün randevusu bulunmaktadır. Bekliyoruz. ${clinicName}`;
    }
    return `Sayın ${item.customer_name}, ${item.pet_name} adlı dostunuzun ${opName} tarihi ${dueDateStr} tarihinde. Randevu için bizi arayın. ${clinicName}`;
  };

  const handleSms = async (e) => {
    e.stopPropagation();
    if (sending) return;
    setSending(true);
    try {
      const res = await sendManualSms({
        phone: item.customer_phone,
        message: getMessage(),
        customer_id: item.customer_id,
        customer_name: item.customer_name,
        pet_name: item.pet_name,
        sms_type: 'manual_reminder',
      });
      if (res.data?.success) {
        toast.success('SMS gönderildi');
      } else {
        toast.error('SMS gönderilemedi');
      }
    } catch {
      toast.error('SMS gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    const waPhone = formatPhoneForWhatsApp(item.customer_phone);
    const text = encodeURIComponent(getMessage());
    window.open(`https://wa.me/${waPhone}?text=${text}`, '_blank');
  };

  return (
    <div className="flex items-center gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={handleSms}
        disabled={sending}
        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-[10px] font-medium transition-colors disabled:opacity-50"
        data-testid={`sms-btn-${item.id}`}
        title="SMS Gönder"
      >
        <MessageCircle size={11} />
        SMS
      </button>
      <button
        onClick={handleWhatsApp}
        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 text-[10px] font-medium transition-colors"
        data-testid={`whatsapp-btn-${item.id}`}
        title="WhatsApp ile Gönder"
      >
        <Send size={11} />
        WA
      </button>
    </div>
  );
};

export const RemindersSection = ({ todaysVaccines, upcomingVaccines, navigate }) => {
  return (
    <div className="lg:grid lg:grid-cols-2 lg:gap-4 space-y-4 lg:space-y-0">
      {/* Today's Reminders */}
      <div className="ios-card animate-fade-in" data-testid="today-reminders-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <AlertCircle size={16} className="text-red-500" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">Bugünün Hatırlatmaları</h2>
          </div>
          {todaysVaccines.length > 0 && (
            <span className="text-xs font-bold text-white bg-red-500 rounded-full px-2 py-0.5 min-w-[24px] text-center">{todaysVaccines.length}</span>
          )}
        </div>
        {todaysVaccines.length > 0 ? (
          <div className="space-y-2">
            {todaysVaccines.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(`/customer/${item.customer_id}`)}
                className="flex items-center justify-between p-3 bg-[#F2F2F7] rounded-xl cursor-pointer hover:bg-gray-200 transition-colors"
                data-testid={`today-item-${item.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center`}>
                    <PetAvatar species={item.pet_species} gender={item.pet_gender} size={22} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{item.pet_name}</p>
                    <div className="flex items-center gap-1">
                      {item.operation_type === 'vaccine' ? (
                        <Syringe size={12} className="text-green-600" />
                      ) : (
                        <Stethoscope size={12} className="text-blue-600" />
                      )}
                      <p className={`text-xs font-medium ${
                        item.operation_type === 'vaccine' ? 'text-green-600' : 'text-blue-600'
                      }`}>{item.operation_name}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{item.customer_name}</p>
                  <a
                    href={`tel:${item.customer_phone}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-[#6b6de3] flex items-center gap-1 justify-end"
                  >
                    <Phone size={12} />
                    {item.customer_phone}
                  </a>
                  <ReminderActions item={item} type="today" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-3">
            <p className="text-[#34C759] text-sm font-medium">Bugün hatırlatma yok</p>
          </div>
        )}
      </div>

      {/* Upcoming Reminders (Next 7 days) */}
      <div className="ios-card animate-fade-in" data-testid="upcoming-reminders-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
              <AlertCircle size={16} className="text-orange-500" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">Yaklaşan Hatırlatmalar</h2>
          </div>
          <div className="flex items-center gap-2">
            {upcomingVaccines.length > 0 && (
              <span className="text-xs font-bold text-white bg-orange-500 rounded-full px-2 py-0.5 min-w-[24px] text-center">{upcomingVaccines.length}</span>
            )}
            {upcomingVaccines.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/records', { state: { tab: 'upcoming' } })}
                className="text-[#6b6de3] text-xs h-auto p-1"
              >
                Tümü
              </Button>
            )}
          </div>
        </div>
        {upcomingVaccines.length > 0 ? (
          <div className="space-y-2">
            {upcomingVaccines.map((item) => (
              <div
                key={item.id}
                onClick={() => item.customer_id && navigate(`/customer/${item.customer_id}`)}
                className="flex items-center justify-between p-3 bg-[#F2F2F7] rounded-xl cursor-pointer hover:bg-gray-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center`}>
                    <PetAvatar species={item.pet_species} gender={item.pet_gender} size={22} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{item.pet_name}</p>
                    <div className="flex items-center gap-1">
                      {item.operation_type === 'vaccine' ? (
                        <Syringe size={12} className="text-green-600" />
                      ) : (
                        <Stethoscope size={12} className="text-blue-600" />
                      )}
                      <p className="text-xs text-[#8E8E93]">{item.operation_name}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{item.customer_name}</p>
                  <p className="text-sm font-medium text-[#FF9500]">
                    {new Date(item.next_due_date).toLocaleDateString('tr-TR')}
                  </p>
                  <p className="text-xs text-[#8E8E93]">{item.customer_phone}</p>
                  <ReminderActions item={item} type="upcoming" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-3">
            <p className="text-[#34C759] text-sm font-medium">7 gün içinde hatırlatma yok</p>
          </div>
        )}
      </div>
    </div>
  );
};
